import { useCallback, useEffect, useRef, useState } from 'react'
import { useNls } from '../../nls/nlsProvider'

export type InstantSettingStatus = 'idle' | 'saving' | 'saved' | 'error'

export interface UseInstantSettingResult {
  status: InstantSettingStatus
  error: string | null
  run: <T>(saveFn: () => Promise<T>) => Promise<T | undefined>
  reset: () => void
}

/**
 * useInstantSetting (SR5)
 * Wraps an async save action for instant toggles/switches/selects.
 * Transitions: saving -> saved -> idle (clears after 1500ms) or saving -> error.
 */
export function useInstantSetting(): UseInstantSettingResult {
  const [status, setStatus] = useState<InstantSettingStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { t } = useNls()

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearTimer()
    }
  }, [clearTimer])

  const reset = useCallback(() => {
    clearTimer()
    setStatus('idle')
    setError(null)
  }, [clearTimer])

  const run = useCallback(
    async <T>(saveFn: () => Promise<T>): Promise<T | undefined> => {
      clearTimer()
      setStatus('saving')
      setError(null)

      try {
        const result = await saveFn()
        setStatus('saved')
        timerRef.current = setTimeout(() => {
          setStatus('idle')
          timerRef.current = null
        }, 1500)
        return result
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('NLSSettingsKitSaveFailed')
        setStatus('error')
        setError(message)
        throw err
      }
    },
    [clearTimer, t],
  )

  return { status, error, run, reset }
}
